import APIService from "./APIService";

/**
 * userService — adapter for the unified User management page (`/User/:id`).
 *
 * The redesigned page manages a user "regardless of type" (Viewer / Account /
 * Manufacturer / Distributor). Today the backend only exposes type-specific
 * endpoints (getaccountuserbyID, getdistributoruserbyID, ...). This adapter
 * centralizes the *unified* contract the page consumes and normalizes whatever
 * the backend returns into a single shape.
 *
 * ─── APIs this expects the backend to provide (see plan) ─────────────────────
 *   GET  getuserbyid?ID={globalUserID}   -> unified user (UserType + fields)
 *   PUT  updateuser                      -> unified update
 *   PUT  inviteuser / resenduserinvite / revokeuseraccess
 *   GET  getuseractivity?ID={globalUserID}
 */

export const USER_TYPES = {
  ADMIN: "Admin",
  ACCOUNT: "Account",
  MANUFACTURER: "Manufacturer",
  DISTRIBUTOR: "Distributor",
};

export const ACCOUNT_USER_TYPE_OPTIONS = [
  { label: "Provider", value: "Provider" },
  { label: "AP", value: "AP" },
  { label: "Claims", value: "Claims" },
  { label: "Purchasing", value: "Purchasing" },
  { label: "Shipping", value: "Shipping" },
];

// Human-readable role name shown next to the user's name in the title bar.
// The former "Viewer" role has been merged into Admin; Admin now carries the
// Region scoping field.
export const USER_ROLE_LABELS = {
  [USER_TYPES.ADMIN]: "Admin",
  [USER_TYPES.ACCOUNT]: "Account User",
  [USER_TYPES.DISTRIBUTOR]: "Distributor User",
  [USER_TYPES.MANUFACTURER]: "Manufacturer User",
};

export const getUserRoleLabel = (user) =>
  (user &&
    (user.IsProvider
      ? "Provider"
      : USER_ROLE_LABELS[user.UserType] || user.RoleLabel)) ||
  "";

// Unified status derived from invite/revoke flags. Matches the flag semantics
// used in GenericDashboard.js (Invited / InviteAccepted) plus a revoked flag.
export const USER_STATUS = {
  NOT_INVITED: "Not Invited",
  INVITE_SENT: "Invite Sent",
  ACTIVE: "Active",
  REVOKED: "Revoked",
};

export const getUserStatus = (user) => {
  if (!user) return USER_STATUS.NOT_INVITED;
  if (user.IsRevoked) return USER_STATUS.REVOKED;
  if (user.InviteAccepted) return USER_STATUS.ACTIVE;
  if (user.Invited) return USER_STATUS.INVITE_SENT;
  return USER_STATUS.NOT_INVITED;
};

// Maps a unified status to the StatusIndicatorHeader statusType (color).
export const getUserStatusType = (status) => {
  switch (status) {
    case USER_STATUS.ACTIVE:
      return "success";
    case USER_STATUS.INVITE_SENT:
      return "warning";
    case USER_STATUS.REVOKED:
      return "failure";
    default:
      return "default";
  }
};

// Normalize an arbitrary backend user payload into the page's shape.
const normalizeUser = (raw = {}) => ({
  EmailChangeEnabled: raw.EmailChangeEnabled === true,
  EmailVersion: raw.EmailVersion ?? 0,
  NotificationStatus: raw.NotificationStatus ?? null,
  NotificationError: raw.NotificationError ?? null,
  UserID: raw.UserID ?? raw.ID ?? null,
  UserType: raw.UserType ?? raw.RoleType ?? null,
  RoleLabel: raw.RoleLabel ?? raw.Role ?? raw.UserRole ?? "",
  AccountContactType: raw.AccountContactType ?? "",
  ProviderNPI: raw.ProviderNPI ?? "",
  Prefix: raw.Prefix ?? "",
  ContactEmail: raw.ContactEmail ?? "",
  IsProvider:
    raw.IsProvider === true ||
    Number(raw.IsProvider) === 1 ||
    raw.AccountContactType === "Provider" ||
    raw.RoleLabel === "Provider",
  FirstName: raw.FirstName ?? "",
  LastName: raw.LastName ?? "",
  Email: raw.Email ?? "",
  Phone: raw.Phone ?? "",
  // status flags
  Invited: Number(raw.Invited) === 1 || raw.Invited === true,
  InviteAccepted:
    Number(raw.InviteAccepted) === 1 || raw.InviteAccepted === true,
  IsRevoked: Number(raw.IsRevoked) === 1 || raw.IsRevoked === true,
  InviteStatus: raw.InviteStatus ?? null,
  InvitedAt: raw.InvitedAt ?? null,
  InvitedByUserID: raw.InvitedByUserID ?? null,
  InviteAcceptedAt: raw.InviteAcceptedAt ?? null,
  InviteAcceptedByUserID: raw.InviteAcceptedByUserID ?? null,
  RevokedAt: raw.RevokedAt ?? null,
  RevokedByUserID: raw.RevokedByUserID ?? null,
  // role-specific associations
  RegionID: raw.RegionID ?? null,
  RegionName: raw.RegionName ?? "",
  AccountID: raw.AccountID ?? null,
  AccountName: raw.AccountName ?? "",
  ManufacturerID: raw.ManufacturerID ?? null,
  ManufacturerName: raw.ManufacturerName ?? "",
  DistributorID: raw.DistributorID ?? null,
  DistributorName: raw.DistributorName ?? "",
  IsManager: Number(raw.IsManager) === 1 || raw.IsManager === true,
});

// Lazy construction avoids initializing the client before application/test setup.
const apiService = {
  getDataFromBackend: (...args) => new APIService().getDataFromBackend(...args),
  updateDataToBackend: (...args) =>
    new APIService().updateDataToBackend(...args),
};

/**
 * Fetch a single user by global UserID.
 * @param {string|number} id
 */
export const getUser = async (id) => {
  const response = await apiService.getDataFromBackend(`getuserbyid?ID=${id}`);
  const data = response?.data?.data;
  if (!data) throw new Error("empty user payload");
  return normalizeUser(data);
};

export const getUserActivity = async (id, beforeID) => {
  const response = await apiService.getDataFromBackend(
    `getuseractivity?ID=${encodeURIComponent(id)}${beforeID ? `&beforeID=${encodeURIComponent(beforeID)}` : ""}`,
  );
  const data = response?.data?.data;
  if (!Array.isArray(data)) throw new Error("no activity payload");
  return Object.assign(data, {
    nextCursor: response?.data?.nextCursor ?? null,
  });
};

export const updateUser = async (payload) => {
  return apiService.updateDataToBackend(payload, "updateuser");
};

export const inviteUser = async (user) => {
  return apiService.updateDataToBackend(
    { UserID: user.UserID, Email: user.Email, UserType: user.UserType },
    "inviteuser",
  );
};

export const resendInvite = async (user) => {
  return apiService.updateDataToBackend(
    { UserID: user.UserID, Email: user.Email, UserType: user.UserType },
    "resenduserinvite",
  );
};

export const revokeUser = async (user) => {
  return apiService.updateDataToBackend(
    { UserID: user.UserID, UserType: user.UserType },
    "revokeuseraccess",
  );
};

// Soft delete: the backend keeps the User row and marks the role row deleted,
// so the portal retains a record of everyone who ever had access.
export const deleteUser = async (user) => {
  return apiService.updateDataToBackend(
    { UserID: user.UserID, UserType: user.UserType },
    "deleteuser",
  );
};

export const getDeletedUsers = async (afterUserID = 0) => {
  const response = await apiService.getDataFromBackend(
    `deletedusers?afterUserID=${afterUserID}`,
  );
  return response.data;
};
export const restoreUser = (payload) =>
  apiService.updateDataToBackend(payload, "restoreuser");
